import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import { FlowContainer, FlowNode, FlowArrow, FlowTerminal, FlowNote, FlowParallel } from '../FlowPrimitives';

// ============================================================================
// Experiment 8 – Spectrophotometric Determination of Nitrite in Water
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp8',
  title: 'Experiment 8: Spectrophotometric Determination of Nitrite in Water',
  objectives: [
    'Apply the Griess reaction for nitrite analysis',
    'Prepare calibration standards from a stock solution',
    'Use Spectronic 20 spectrophotometry at 510 nm',
    'Analyze environmental water samples for nitrite content',
  ],
  safetyNotes: [
    "Marshall\u2019s reagent (Griess reagent) is mildly irritating — avoid skin contact",
    'Wear gloves and safety glasses at all times',
    'Work in a well-ventilated area',
    'Dispose of chemical waste in designated containers',
  ],
  equipment: [
    'Spectronic 20 spectrophotometer',
    'Spectronic 20 cuvette (same cuvette for all readings)',
    'Volumetric flasks (100 mL)',
    'Pipettes (various sizes)',
    'Timer',
  ],
  chemicals: [
    'Nitrite standard solution (10 ppm NO₂⁻)',
    "Marshall\u2019s reagent (Griess reagent)",
    'Distilled water',
    'Water samples for analysis',
  ],
  procedure: [
    {
      number: 1,
      title: 'Prepare Standards',
      details: [
        'Prepare a blank (0 ppm) and five standards: 0.5, 1.0, 1.5, 2.0, 2.5 ppm NO₂⁻',
        'Pipette the required volume of 10 ppm stock into separate 100 mL volumetric flasks',
        'Blank = 0 mL stock, Std 1 = 0.5 mL, Std 2 = 1.0 mL, etc.',
      ],
      manualRef: 'Lab Manual p.1',
    },
    {
      number: 2,
      title: "Add Marshall\u2019s Reagent (Griess Reagent)",
      details: [
        "Add 4 mL Marshall\u2019s reagent to each standard/blank flask; add 1 mL to each unknown water sample",
        'Dilute to the 100 mL mark with distilled water',
        'Mix thoroughly by inverting several times',
      ],
      safetyNote: 'Avoid skin contact with reagent',
      manualRef: 'Lab Manual p.1',
    },
    {
      number: 3,
      title: 'Develop Color (20 Minutes)',
      details: [
        'Start timer immediately after mixing',
        'Wait exactly 20 minutes for full color development',
        'Do NOT measure before 20 minutes — will underestimate concentration',
        'Color is stable after 20 minutes for several hours',
      ],
      safetyNote: 'Timing is critical — start timer as soon as reagent is added',
      manualRef: 'Lab Manual p.1–2',
    },
    {
      number: 4,
      title: 'Measure Absorbance',
      details: [
        'Set spectrophotometer wavelength to 510 nm',
        'Zero the instrument with the blank solution',
        'Measure absorbance of all standards',
        'Measure absorbance of all water samples',
      ],
      manualRef: 'Lab Manual p.2',
    },
    {
      number: 5,
      title: 'Build Calibration Curve',
      details: [
        'Plot absorbance (y-axis) vs. NO₂⁻ concentration in ppm (x-axis)',
        'Fit a linear trendline and obtain the equation: A = slope × C + intercept',
        'Record R\u00b2 value — should be > 0.99',
      ],
      manualRef: 'Lab Manual p.2',
    },
    {
      number: 6,
      title: 'Analyze Samples',
      details: [
        'Use calibration equation: [NO₂⁻] = (Absorbance – intercept) / slope',
        'Apply dilution factor if sample was diluted',
        'Compare result to EPA drinking-water limit of 1.0 ppm NO₂⁻',
      ],
      manualRef: 'Lab Manual p.2',
    },
  ],
  keyFormulas: [
    '[NO₂⁻] = (Absorbance – intercept) / slope',
    '[NO₂⁻]_original = [NO₂⁻]_measured × dilution factor',
    'Griess: Sulfanilamide + NO₂⁻ → Diazonium + NED → Azo dye (λmax = 510 nm)',
  ],
  tips: [
    'The 20-minute wait is the single most critical step — set a timer',
    'If sample absorbance exceeds the highest standard, dilute and re-measure',
    'Always zero the spectrophotometer with the blank before each measurement run',
    'Use the same Spectronic 20 cuvette for all readings; condition with next standard after each reading',
    'EPA limit for nitrite in drinking water is 1.0 ppm',
    'Sources of nitrite include agricultural runoff, sewage, and industrial waste',
  ],
};

// ============================================================================
// Flowchart — Nitrite Determination Procedure
// ============================================================================

function Flowchart() {
  const nodeW = 180;
  const nodeH = 56;
  const cx = 210; // left edge of centred node (210 + 180/2 = 300 centre)
  const startY = 44;
  const gap = 76;

  const row = (i: number) => startY + i * gap;

  return (
    <FlowContainer width={600} height={620} title="Nitrite Determination — Griess Reaction">
      {/* Start */}
      <FlowTerminal x={230} y={row(0)} width={140} height={36} label="Start" color="#6366f1" />

      {/* Step 1 — Prepare Standards */}
      <FlowArrow from={{ x: 300, y: row(0) + 36 }} to={{ x: 300, y: row(1) }} />
      <FlowNode x={cx} y={row(1)} width={nodeW} height={nodeH} title="Prepare Standards" subtitle="0, 0.5, 1.0, 1.5, 2.0, 2.5 ppm" color="#0d9488" />

      {/* Step 2 — Add Griess Reagent */}
      <FlowArrow from={{ x: 300, y: row(1) + nodeH }} to={{ x: 300, y: row(2) }} />
      <FlowNode x={cx} y={row(2)} width={nodeW} height={nodeH} title="Add Griess Reagent" subtitle="4 mL to stds/blank, 1 mL to unknowns" color="#0d9488" />

      {/* Step 3 — Develop Color */}
      <FlowArrow from={{ x: 300, y: row(2) + nodeH }} to={{ x: 300, y: row(3) }} />
      <FlowNode x={cx} y={row(3)} width={nodeW} height={nodeH} title="Develop Color" subtitle="Wait exactly 20 minutes" color="#f59e0b" />
      <FlowNote x={400} y={row(3) + 4} text="20 min — CRITICAL" width={130} color="#ef4444" />

      {/* Step 4 — Measure Absorbance */}
      <FlowArrow from={{ x: 300, y: row(3) + nodeH }} to={{ x: 300, y: row(4) }} />
      <FlowNode x={cx} y={row(4)} width={nodeW} height={nodeH} title="Measure Absorbance" subtitle="λ = 510 nm, zero with blank" color="#0d9488" />

      {/* Step 5 — Build Calibration Curve */}
      <FlowArrow from={{ x: 300, y: row(4) + nodeH }} to={{ x: 300, y: row(5) }} />
      <FlowNode x={cx} y={row(5)} width={nodeW} height={nodeH} title="Build Cal. Curve" subtitle="A vs [NO₂⁻], linear fit" color="#0d9488" />

      {/* Step 6 — Analyze Sample */}
      <FlowArrow from={{ x: 300, y: row(5) + nodeH }} to={{ x: 300, y: row(6) }} />
      <FlowNode x={cx} y={row(6)} width={nodeW} height={nodeH} title="Analyze Sample" subtitle="[NO₂⁻] from calibration" color="#0d9488" />

      {/* End */}
      <FlowArrow from={{ x: 300, y: row(6) + nodeH }} to={{ x: 300, y: row(6) + nodeH + 20 }} />
      <FlowTerminal x={230} y={row(6) + nodeH + 20} width={140} height={36} label="Done" color="#6366f1" />
    </FlowContainer>
  );
}

// ============================================================================
// Default Export
// ============================================================================

export default function Exp8Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
